import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UndefinedInitialQueryOptions,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import axiosInstance from '@/plugins/axios';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import type {
  IFlatFolderItem,
  IFolderItem,
  IFolderTreeResponse,
  IHashedFolderItem,
  ISettingsDeleteFolderRequest,
  ISettingsFolderCreateResponse,
  ISettingsFolderRequest,
  ISettingsFolderUpdateOrderRequest,
  ISettingsUpdateFolderRequest
} from '@/types/services/folder.service';
import type { DefaultError } from '@tanstack/query-core';
import type { EFolderSpecialUse } from '@/types/services/folder.service.enums';
import useSnackbar from '@/composables/useSnackbar';
import { useI18n } from 'vue-i18n';
import { v4 as uuid } from 'uuid';

const recursiveFlattenFolderTree = (
  folderItems: IFolderItem[],
  parentName?: string
): IFlatFolderItem[] => {
  return folderItems.flatMap((item): IFlatFolderItem[] => {
    const children = item.children;
    const clone: IFlatFolderItem = {
      ...item,
      children: undefined,
      fullName: (parentName ? `${parentName}/` : '') + item.name
    };
    if (children.length) {
      return [clone, ...recursiveFlattenFolderTree(children, clone.fullName)];
    }
    return [clone];
  });
};

const recursiveMapHashToFolderTreeItem = (folders: IFolderItem[]): IHashedFolderItem[] => {
  return folders.map((folder) => {
    const children = folder.children;
    return {
      ...folder,
      hash: uuid(),
      children: children.length ? recursiveMapHashToFolderTreeItem(children) : []
    };
  });
};

const useFoldersService = () => {
  const { t, locale } = useI18n();
  const { addSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();

  const folderTreeQueryOptions = (): UndefinedInitialQueryOptions<
    { hash: string; data: IFolderItem[] },
    Error,
    IFolderTreeResponse
  > =>
    queryOptions<{ hash: string; data: IFolderItem[] }, Error, IFolderTreeResponse>({
      queryKey: ['authenticatedQuery', userIndex, 'folders', locale],
      queryFn: async ({ signal }) => {
        return {
          hash: uuid(),
          data: (
            await axiosInstance.get<IFolderItem[]>(`/u/${userIndex.value}/mailbox`, { signal })
          ).data
        };
      },
      enabled: () => queriesEnabled.value,
      // placeholderData: keepPreviousData,
      select: (data) => {
        return { ...data, data: recursiveMapHashToFolderTreeItem(data.data) };
      }
    });
  const folderFlatQueryOptions = (): UndefinedInitialQueryOptions<IFlatFolderItem[]> =>
    queryOptions<IFlatFolderItem[]>({
      queryKey: ['authenticatedQuery', userIndex, 'folders', 'flat', locale],
      queryFn: async () => {
        // Get the folder tree
        const responseData = await queryClient.fetchQuery(folderTreeQueryOptions());

        // Create flat folder list
        const flatData = recursiveFlattenFolderTree(responseData.data);
        return flatData;
      },
      enabled: () => queriesEnabled.value
      // placeholderData: keepPreviousData
    });

  const getFolderWithSpecialUseQueryOptions = (
    specialUse: EFolderSpecialUse
  ): UndefinedInitialQueryOptions<IFlatFolderItem | null> =>
    queryOptions<IFlatFolderItem | null>({
      queryKey: ['authenticatedQuery', userIndex, 'folders', 'specialUse', locale, specialUse],
      queryFn: async () => {
        // Get the folder list
        const flatFolders = await queryClient.fetchQuery(folderFlatQueryOptions());

        // Find folder with special use
        const res = flatFolders?.find(
          (folder: IFlatFolderItem) =>
            folder.specialUse === specialUse || folder.optionalSpecialUseFolder === specialUse
        );

        if (!res) {
          return null;
        }
        return res;
      },
      enabled: () => queriesEnabled.value
    });

  const getFolderTree = (): UseQueryReturnType<IFolderTreeResponse, DefaultError> => {
    return useQuery(folderTreeQueryOptions());
  };

  const getFlatFolders = (): UseQueryReturnType<IFlatFolderItem[], DefaultError> => {
    return useQuery(folderFlatQueryOptions());
  };

  const getFolderWithSpecialUse = (
    specialUse: EFolderSpecialUse
  ): UseQueryReturnType<IFlatFolderItem | null, Error> => {
    return useQuery(getFolderWithSpecialUseQueryOptions(specialUse));
  };

  const createFolder = () => {
    return useMutation<ISettingsFolderCreateResponse, DefaultError, ISettingsFolderRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'mailbox', 'folders', 'create'],
      mutationFn: async (data) => {
        return (
          await axiosInstance.post<ISettingsFolderCreateResponse>(
            `/u/${userIndex.value}/mailbox/folders`,
            data
          )
        ).data;
      },
      onSuccess: () => {
        addSnackbar({ type: 'success', message: t('settings.folders.success.saved') });
      }
    });
  };

  const updateFolder = () => {
    return useMutation<
      void,
      DefaultError,
      { id: ISettingsUpdateFolderRequest['id']; data: ISettingsFolderRequest }
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'mailbox', 'folders', 'update'],
      mutationFn: async ({ id, data }) => {
        await axiosInstance.patch(`/u/${userIndex.value}/mailbox/folders/${id}`, data);
      }
    });
  };

  const deleteFolder = () => {
    return useMutation<void, DefaultError, ISettingsDeleteFolderRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'mailbox', 'folders', 'delete'],
      mutationFn: async ({ id }) => {
        await axiosInstance.delete(`/u/${userIndex.value}/mailbox/folders/${id}`);
      }
      // onSuccess: (_, variables) => {
      //   addSnackbar({
      //     type: 'success',
      //     message: t('settings.folders.success.deleted', {
      //       name: variables.name
      //     })
      //   });
      // }
    });
  };

  const updateFolderOrder = () => {
    return useMutation<void, DefaultError, ISettingsFolderUpdateOrderRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'mailbox', 'folders', 'update'],
      mutationFn: async (data) => {
        await axiosInstance.patch(`/u/${userIndex.value}/mailbox/folders/updateOrder`, data);
      }
    });
  };

  /**
   * Invalidates all folder queries (tree, flat list and special-use lookups, for every locale)
   * so the current folder counters are refetched from the server. Local actions that change
   * folder contents (sending or deleting a draft) must call this, because the COUNTERS event of
   * the updates stream that carries the new counters can be lost without any automatic refetch,
   * so the folder badges must not depend on it.
   */
  const invalidateFolders = (): void => {
    if (!userIndex.value) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: ['authenticatedQuery', userIndex.value, 'folders']
    });
  };

  return {
    getFolderTree,
    getFolderWithSpecialUse,
    getFlatFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    updateFolderOrder,
    getFolderWithSpecialUseQueryOptions,
    invalidateFolders
  };
};

export default useFoldersService;
